import type { PackVersion } from '@planuze/admin-contracts';
import { EmptyState } from '@planuze/ui';
import { ShieldOffIcon } from 'lucide-react';
import { useMemo } from 'react';
import { usePacksCatalogContent } from '../../content/sections/packs-catalog.js';
import { fmtDateTime } from '../../lib/format.js';

/* Histórico de takedown do pack (Fase 2, ADR-0703+). FONTE: os metadados de takedown do
   version-index (versões `revoked` com takedownAt/By/Reason), vindos do endpoint de detalhe.

   POR QUE NÃO o audit-log (targetType=pack): o `pack.takedown` é auditado com `targetId=version`,
   não packId (ADR-0464) — logo `GET /v1/admin/audit-log?targetType=pack&targetId=<packId>` não
   retorna nada por pack. O version-index é a trilha de enforcement autoritativa e per-pack. */
export function PackTakedownHistoryTab({
  versions,
}: {
  readonly versions: readonly PackVersion[];
}) {
  const copy = usePacksCatalogContent().takedownHistory;
  const revoked = useMemo(
    () =>
      versions
        .filter((version) => version.status === 'revoked')
        .sort((a, b) => (b.takedownAt ?? '').localeCompare(a.takedownAt ?? '')),
    [versions],
  );

  if (revoked.length === 0) {
    return (
      <EmptyState
        icon={<ShieldOffIcon className="size-6" />}
        title={copy.emptyTitle}
        description={copy.emptyDescription}
      />
    );
  }

  return (
    <ul className="space-y-2">
      {revoked.map((version) => (
        <li
          key={version.version}
          className="rounded-md border border-border bg-card p-3 space-y-1.5"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-mono text-sm text-foreground">v{version.version}</span>
            {version.takedownAt !== undefined && (
              <span className="text-xs text-muted-foreground">
                {fmtDateTime(version.takedownAt)}
              </span>
            )}
          </div>
          <p className="text-sm text-foreground">{version.takedownReason ?? copy.reasonMissing}</p>
          {version.takedownBy !== undefined && (
            <p className="text-xs text-muted-foreground">
              {copy.byPrefix} <span className="font-mono">{version.takedownBy}</span>
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}
